import { useEffect, useState, useCallback } from 'react';

import useAnalytics from '../../lib/segment/useAnalytics';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@nerdwallet/currency/components/Button';
import { Link } from '@nerdwallet/currency/components/Link';

import { getLogger } from '@nerdwallet/logging';
import type { ReduxStore } from '../redux/types';
import { REDIRECT_TO_HOME_TAXES_URLS } from '../lib/constants';
import { SET_GLOBAL_NAV_STATE } from '../redux/actions';
import type { AuthParams } from '@nerdwallet/nw-auth-links';

import LinkGenerator from '@nerdwallet/nw-auth-links';
import { getCookie } from '../../util/cookies';
import { Text } from '../components/text/Text';
import { makeQueryString } from '../../../pages/helpers';
import { transformTaxonomy } from '../../lib/helpers';

const rootLogger = getLogger('TrackedButton');

const buttonTypes = ['signin', 'register'] as const;

/* Returns a valid button type. */
const validateButtonType = (type: (typeof buttonTypes)[number]) => {
  const logger = getLogger('validateButtonType', rootLogger);
  if (buttonTypes.includes(type)) {
    return type;
  }
  logger.error('Error finding valid button type. Defaulting to register type.');
  return 'register';
};

interface Props {
  buttonName?: string;
  buttonType?: (typeof buttonTypes)[number];
  handleClick?: () => void;
  isPrimary?: boolean;
  isTertiary?: boolean;
  styleClasses?: string;
  isLink?: boolean;
  cohort?: string;
  redirectQueryParam?: string;
  driverLocation?: string;
  authParamsOverride?: AuthParams;
  overrideLink?: string;
}

const TrackedButton: React.FC<Props> = ({
  buttonName = 'Register',
  buttonType = 'register',
  handleClick,
  isPrimary = false,
  isTertiary = false,
  styleClasses,
  isLink,
  cohort,
  redirectQueryParam = '',
  driverLocation = '',
  overrideLink,
}) => {
  const pageTaxonomy = useSelector<ReduxStore, ReduxStore['pageTaxonomy']>(
    (store) => store.pageTaxonomy
  );

  const authParamsOverride = useSelector<
    ReduxStore,
    ReduxStore['authParamsOverride']
  >((store) => store.authParamsOverride);

  const segmentTrackingAttributes = useSelector<
    ReduxStore,
    ReduxStore['segmentTrackingAttributes']
  >((store) => store.segmentTrackingAttributes);

  const [btnType, setBtnType] = useState(validateButtonType(buttonType));

  /* Sets btnType state and revalidates if buttonType prop changes. */
  useEffect(() => {
    const type = validateButtonType(buttonType);
    setBtnType(type);
  }, [buttonType]);

  const enrichedSegmentTrackingAttributes = {
    anonymousUserId: segmentTrackingAttributes?.anonymousUserId,
    deployableName: segmentTrackingAttributes?.deployableName,
    deployableVersion: segmentTrackingAttributes?.deployableVersion,
    driverContentId: segmentTrackingAttributes?.driverContentId,
    driverContentSource: segmentTrackingAttributes?.driverContentSource,
    driverElement: 'button',
    driverFlowName: segmentTrackingAttributes?.driverFlowName,
    driverLocation,
    driverPageName: segmentTrackingAttributes?.driverPageName,
    driverPageUrl: window?.location?.href,
    driverPageViewId: segmentTrackingAttributes?.driverPageViewId,
    driverSubLocation: cohort,
    mostRecentTraditionalDeviceId: getCookie('cookie_id'),
  };

  // Get anonymous ID from segment to pass to Auth0.
  // Getting anon ID from segment is async, so this must happen in a useEffect.
  const { getAnonymousId } = useAnalytics();

  const setHeaderPropsDispatch = useDispatch();

  const setHeaderProps = useCallback(
    (props) => {
      setHeaderPropsDispatch({
        ...props,
        type: SET_GLOBAL_NAV_STATE,
      });
    },
    [setHeaderPropsDispatch]
  );

  useEffect(() => {
    async function setAnonymousId() {
      const anonymousUserId = await getAnonymousId();
      if (
        anonymousUserId !== enrichedSegmentTrackingAttributes.anonymousUserId
      ) {
        setHeaderProps({
          segmentTrackingAttributes: { anonymousUserId },
        });
      }
    }
    setAnonymousId();
  }, [enrichedSegmentTrackingAttributes]);

  const redirectToTaxHome = REDIRECT_TO_HOME_TAXES_URLS.includes(
    window.location.pathname
  );
  const myNerdWalletRedirectPage = redirectToTaxHome ? 'taxes' : 'dashboard';

  const ntsParams = {
    // Transform feat_bypass_mfa query param to bypassMFA for NTS
    bypassMFA: new URLSearchParams(window.location.search).get(
      'feat_bypass_mfa'
    ),
    redirectTo: `${window?.location?.protocol}//${window?.location?.host}/home/${myNerdWalletRedirectPage}${redirectQueryParam}`,
    initialScreen: '',
  };
  if (btnType === 'register') {
    ntsParams.initialScreen = 'signup';
  }

  const params = {
    // LinkGenerator expects redirectTo prop, so add it here
    redirectTo: '',
    ...enrichedSegmentTrackingAttributes,
    ...ntsParams,
    ...authParamsOverride,
  };

  const linkGenerator = new LinkGenerator();

  const generatedLink =
    buttonType === 'register'
      ? linkGenerator.register(params)
      : linkGenerator.login(params);

  const link = makeQueryString({
    url: generatedLink.toString(),
    taxonomy: transformTaxonomy(pageTaxonomy),
  });

  /* Opens a signin or register modal. */
  const handleAuthClick = () => {
    // if showAuthDialog is available use that - otherwise let the
    // user follow the link
    if (handleClick) {
      handleClick();
    }
  };

  if (isLink) {
    return (
      <Link
        href={overrideLink || link}
        onClick={handleAuthClick}
        unstyled
        className={styleClasses}
      >
        <Text size="small" tone="light" bold>
          {buttonName}
        </Text>
      </Link>
    );
  }

  return (
    <Button
      href={overrideLink || link}
      rel="nofollow noindex"
      onClick={handleAuthClick}
      primary={isPrimary}
      tertiary={isTertiary}
      className={styleClasses}
    >
      {buttonName}
    </Button>
  );
};

export default TrackedButton;
